import { gql } from "@apollo/client"

// ============================================================
// FRAMMENTI RIUTILIZZABILI
// ============================================================

// Tutti i campi di una variante prodotto (colore, taglia, prezzo, ecc.)
const VARIANT_FIELDS = gql`
  fragment VariantFields on ProductVariant {
    id
    title
    availableForSale
    selectedOptions {
      name
      value
    }
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
    image {
      url
      altText
    }
  }
`

// Tutti i campi del carrello (usati da query + tutte le mutations)
const CART_FIELDS = gql`
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    discountCodes { code applicable }
    # gli sconti che Shopify ha gia' calcolato: li stampa lo scontrino
    discountAllocations {
      discountedAmount { amount currencyCode }
      ... on CartCodeDiscountAllocation { code }
      ... on CartAutomaticDiscountAllocation { title }
    }
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }
    lines(first: 50) {
      edges {
        node {
          id
          quantity
          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }
          discountAllocations {
            discountedAmount { amount currencyCode }
            ... on CartCodeDiscountAllocation { code }
            ... on CartAutomaticDiscountAllocation { title }
          }
          merchandise {
            ... on ProductVariant {
              id
              title
              selectedOptions { name value }
              price { amount currencyCode }
              image { url altText }
              product { title handle }
            }
          }
        }
      }
    }
  }
`

// ============================================================
// QUERY PRODOTTI
// ============================================================

// Prodotti completi: opzioni (Colore/Taglia/...), tutte le varianti, più immagini.
// Usata sia dalle scatole 3D (ProductsHandler) sia dalla pagina prodotto.
// Politiche del negozio (privacy, resi, termini, spedizioni):
// configurate in Shopify admin → Impostazioni → Politiche.
// Le voci non compilate arrivano null e non vengono mostrate.
export const GET_SHOP_POLICIES = gql`
  query GetShopPolicies {
    shop {
      privacyPolicy { url title }
      refundPolicy { url title }
      termsOfService { url title }
      shippingPolicy { url title }
    }
  }
`

export const GET_PRODUCTS = gql`
  ${VARIANT_FIELDS}
  query GetProducts {
    # CREATED_AT: ordine stabile — i prodotti nuovi si accodano senza
    # rimescolare le posizioni di quelli già disposti sugli scaffali
    products(first: 50, sortKey: CREATED_AT) {
      edges {
        node {
          id
          title
          handle
          description
          collections(first: 5) {
            edges {
              node {
                handle
                title
              }
            }
          }
          options {
            name
            values
          }
          images(first: 10) {
            edges {
              node {
                url
                altText
              }
            }
          }
          # Media del prodotto: video (mp4 multi-qualità) e modelli 3D
          # (GLB caricato in admin → sostituisce la scatola generica)
          media(first: 10) {
            edges {
              node {
                mediaContentType
                ... on Video {
                  sources {
                    url
                    mimeType
                  }
                  previewImage {
                    url
                  }
                }
                ... on Model3d {
                  sources {
                    url
                    format
                  }
                }
              }
            }
          }
          variants(first: 50) {
            edges {
              node {
                ...VariantFields
              }
            }
          }
        }
      }
    }
  }
`

// ============================================================
// CART API (Storefront) — carrello vero di Shopify
// ============================================================

export const GET_CART = gql`
  ${CART_FIELDS}
  query GetCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartFields
    }
  }
`

export const CART_CREATE = gql`
  ${CART_FIELDS}
  mutation CartCreate {
    cartCreate {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`

export const CART_LINES_ADD = gql`
  ${CART_FIELDS}
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`

export const CART_LINES_UPDATE = gql`
  ${CART_FIELDS}
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`

// Codici sconto sul carrello (i premi della macchinetta): la lista
// SOSTITUISCE quella che c'era, quindi va passata intera.
export const CART_DISCOUNT_CODES_UPDATE = gql`
  ${CART_FIELDS}
  mutation CartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`

export const CART_LINES_REMOVE = gql`
  ${CART_FIELDS}
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`
